import { createContext, type FC, type PropsWithChildren, useContext, useEffect, useMemo } from "react";
import { useAudioPlayer } from "react-use-audio-player";
import { getTerminalConfig } from "../config/terminalConfig";
import { howlerState, networkTrace, shouldTraceNetworkDebug } from "../utils/debug/networkTrace";

type AudioPlayer = ReturnType<typeof useAudioPlayer>;

interface AlarmAudioContextValue {
	alarm1: AudioPlayer;
	alarm2: AudioPlayer;
	alarm3: AudioPlayer;
}

const AlarmAudioContext = createContext<AlarmAudioContextValue | null>(null);

/**
 * Loads the three open-door alarm sounds ONCE for the whole app session and holds the players for its
 * lifetime, so every order/refill reuses them instead of re-fetching. `useOpenDoorAlarm` (mounted per
 * order in OrderStep3) only drives playback through these players — it never loads or unloads them.
 *
 * Why app-level and not per-order (the old behaviour, loaded inside OrderStep3): the kiosk runs with the
 * HTTP cache disabled (so it always pulls a fresh app build + fresh data), which meant every OrderStep3
 * mount re-downloaded all three MP3s (~7 MB). On a fridge that does hundreds of orders a day — and
 * especially during back-to-back autotest orders (~one order / 2 s) — that re-download was a large share
 * of the device's network traffic. The app itself only reloads ~once a day, so loading here collapses
 * the audio cost to a single ~7 MB fetch per app session.
 *
 * `html5: true` is load-bearing for TWO reasons — do not revert it to plain Web Audio:
 *   1. Audio: it plays each alarm through an HTML5 <audio> element. The default (Web Audio API) leaves
 *      the AudioContext suspended until a real touch/click unlocks it — but on these kiosks the user
 *      interacts via a physical RFID card + door and often never taps the screen, so the context is
 *      never unlocked and .play() is silently dropped (the warning still shows because it's pure React
 *      state). The <audio> element plays without a gesture under the kiosk WKWebView media setting, so
 *      the alarm is actually audible.
 *   2. Memory: it streams instead of decoding the whole file into a Web Audio AudioBuffer (the buffers
 *      were ~20 MB each, uncompressed PCM). Streaming keeps the resident cost of holding all three
 *      players for the entire session small — a door-open nag alarm does not need Web Audio.
 */
export const AlarmAudioProvider: FC<PropsWithChildren> = ({ children }) => {
	const { firstAlarm, secondAlarm, thirdAlarm } = getTerminalConfig().alarms;

	// useAudioPlayer returns a stable instance per slot for the provider's lifetime; the provider is
	// mounted once near the app root, so these three players persist across every order.
	const alarm1 = useAudioPlayer();
	const alarm2 = useAudioPlayer();
	const alarm3 = useAudioPlayer();
	const { load: alarm1Load, cleanup: alarm1Cleanup } = alarm1;
	const { load: alarm2Load, cleanup: alarm2Cleanup } = alarm2;
	const { load: alarm3Load, cleanup: alarm3Cleanup } = alarm3;

	// Preload exactly once at app start. The load fns (stable players) and the Config sound URLs don't
	// change for the provider's lifetime; re-running this effect would re-fetch the MP3s, which is
	// exactly what app-level loading avoids.
	// biome-ignore lint/correctness/useExhaustiveDependencies: load once for the app session (see above)
	useEffect(() => {
		// TEMP: when ?networkDebug=true, attach onload/onplay tracers so the kiosk overlay shows whether
		// each <audio> element actually loads and whether Howler ever reports playback starting.
		const traceAudio = shouldTraceNetworkDebug();
		const opts = (label: string) =>
			traceAudio
				? {
						loop: true,
						html5: true,
						onload: () => networkTrace(`[audio] ${label} loaded`),
						onplay: () => networkTrace(`[audio] ${label} onplay; ${howlerState()}`),
					}
				: { loop: true, html5: true };
		alarm1Load(firstAlarm.sound, opts("alarm1"));
		alarm2Load(secondAlarm.sound, opts("alarm2"));
		alarm3Load(thirdAlarm.sound, opts("alarm3"));
		if (traceAudio) {
			networkTrace(`[audio] preloaded 3 alarms at app start (html5); ${howlerState()}`);
		}

		// Release the three <audio> elements back to Howler's shared 10-slot HTML5 pool on unmount.
		// The provider is meant to mount once for the session, so this normally never runs — but a soft
		// remount (observed hourly on the kiosk) re-obtains three fresh pool nodes each time. Without
		// releasing the old ones, every remount leaks three nodes; after ~3 remounts the pool drains and
		// Howler hands back a locked <audio> node, silently killing the open-door alarm on the gesture-less
		// kiosk. cleanup -> destroyHowl -> howl.unload() is the only path that returns a node to the pool.
		return () => {
			alarm1Cleanup();
			alarm2Cleanup();
			alarm3Cleanup();
		};
	}, []);

	const value = useMemo<AlarmAudioContextValue>(
		() => ({ alarm1: alarm1, alarm2: alarm2, alarm3: alarm3 }),
		[alarm1, alarm2, alarm3],
	);

	return <AlarmAudioContext.Provider value={value}>{children}</AlarmAudioContext.Provider>;
};

export const useAlarmAudio = (): AlarmAudioContextValue => {
	const context = useContext(AlarmAudioContext);
	if (context === null) {
		throw new Error("useAlarmAudio must be used within an AlarmAudioProvider");
	}
	return context;
};
